import { useMemo, useState } from "react";
import { store, useTick } from "../../plc-sim/store";
import { emptyCell, emptyRow, newNetwork, type Cell, type Network } from "../../plc-sim/types";
import { GROUP_ORDER, TOOL_SPECS, makeCell, type ToolSpec } from "./LadderView";

const BLOCK_W = 110;
const BLOCK_H = 64;

function ensureColumn(net: Network, c: number) {
  const cols = Math.max(1, ...net.rows.map((row) => row.length));
  const required = Math.max(cols, c + 1);
  for (const row of net.rows) while (row.length < required) row.push(emptyCell());
  for (let r = 0; r < Math.max(0, net.rows.length - 1); r++) {
    net.links[r] ??= [];
    while (net.links[r]!.length < required + 1) net.links[r]!.push(false);
  }
}

function keepSpareColumn(net: Network, usedColumn: number) {
  const cols = Math.max(1, ...net.rows.map((row) => row.length));
  if (usedColumn < cols - 1) return;
  for (const row of net.rows) row.push(emptyCell());
  for (let r = 0; r < Math.max(0, net.rows.length - 1); r++) {
    net.links[r] ??= [];
    while (net.links[r]!.length < cols + 2) net.links[r]!.push(false);
  }
}

function visibleCols(net: Network) {
  let last = -1;
  for (const row of net.rows) {
    row.forEach((cell, c) => {
      if (cell.type !== "EMPTY" || cell.operand) last = Math.max(last, c);
    });
  }
  return Math.min(Math.max(2, last + 2), Math.max(2, ...net.rows.map((r) => r.length)));
}

function blockTitle(cell: Cell) {
  if (cell.type === "EMPTY") return "+ BLOCK";
  if (["MATH", "WORD_LOGIC", "CONVERT", "BOOL", "SELECT", "PROGRAM", "MOVE", "CMP"].includes(cell.type)) {
    return cell.op || cell.type;
  }
  if (cell.type === "COIL") return "=";
  if (cell.type === "COIL_NOT") return "=/";
  if (cell.type === "SET") return "S";
  if (cell.type === "RESET") return "R";
  return cell.type;
}

function blockLines(cell: Cell) {
  switch (cell.type) {
    case "EMPTY": return ["اضغط لإضافة بلوك"];
    case "NO": return [`IN  ${cell.operand}`, "CONTACT NO"];
    case "NC": return [`IN  ${cell.operand}`, "CONTACT NC"];
    case "P": return [`CLK ${cell.operand}`, "R_TRIG"];
    case "N": return [`CLK ${cell.operand}`, "F_TRIG"];
    case "COIL":
    case "COIL_NOT":
    case "SET":
    case "RESET": return [`OUT ${cell.operand}`];
    case "TON":
    case "TOF":
    case "TP":
    case "TONR": return [`IN`, `PT  ${cell.preset ?? ""}`, `Q   ${cell.operand}.Q`];
    case "CTU":
    case "CTD": return [`CU/CD`, `PV  ${cell.preset ?? ""}`, `CV  ${cell.operand}.CV`];
    case "CTUD": return [`CU  EN`, `CD  ${cell.b ?? ""}`, `PV  ${cell.preset ?? ""}`];
    case "SR":
    case "RS": return [`S   ${cell.preset ?? ""}`, `R   ${cell.b ?? ""}`, `Q   ${cell.operand}`];
    case "BOOL": return [`IN1 ${cell.preset ?? ""}`, cell.op === "NOT" ? "" : `IN2 ${cell.b ?? ""}`, `OUT ${cell.operand}`].filter(Boolean);
    case "CMP": {
      if (cell.op === "IN_RANGE" || cell.op === "OUT_RANGE") return [`VALUE ${cell.operand}`, `MIN ${cell.b ?? ""}`, `MAX ${cell.c ?? ""}`];
      return [`IN1 ${cell.operand}`, `IN2 ${cell.b ?? ""}`];
    }
    case "MOVE": return [`IN  ${cell.preset ?? ""}`, `OUT ${cell.operand}`];
    case "SELECT": return [`G   ${cell.reset ?? ""}`, `IN0 ${cell.preset ?? ""}`, `IN1 ${cell.b ?? ""}`, `OUT ${cell.operand}`];
    case "MATH":
    case "WORD_LOGIC": return [`IN1 ${cell.preset ?? ""}`, cell.b ? `IN2 ${cell.b}` : "", `OUT ${cell.operand}`].filter(Boolean);
    case "LIMIT": return [`MIN ${cell.preset ?? ""}`, `IN  ${cell.b ?? ""}`, `MAX ${cell.c ?? ""}`, `OUT ${cell.operand}`];
    case "CONVERT": return [`IN  ${cell.preset ?? ""}`, cell.b ? `MIN ${cell.b}` : "", cell.c ? `MAX ${cell.c}` : "", `OUT ${cell.operand}`].filter(Boolean);
    case "PROGRAM": return [cell.operand || "LBL1"];
    default: return [cell.operand];
  }
}

function fbdPorts(cell: Cell): { left: string[]; right: string[] } {
  switch (cell.type) {
    case "EMPTY": return { left: [], right: [] };
    case "TON":
    case "TOF":
    case "TP":
    case "TONR": return { left: ["IN", "PT"], right: ["Q", "ET"] };
    case "CTU":
    case "CTD": return { left: [cell.type === "CTU" ? "CU" : "CD", "R", "PV"], right: ["Q", "CV"] };
    case "CTUD": return { left: ["CU", "CD", "R", "LD", "PV"], right: ["QU", "QD", "CV"] };
    case "SR":
    case "RS": return { left: ["S", "R"], right: ["Q"] };
    case "BOOL": return { left: cell.op === "NOT" ? ["IN"] : ["IN1", "IN2"], right: ["OUT"] };
    case "CMP": return { left: cell.op === "IN_RANGE" || cell.op === "OUT_RANGE" ? ["MIN", "IN", "MAX"] : ["IN1", "IN2"], right: ["Q"] };
    case "MOVE": return { left: ["IN"], right: ["OUT"] };
    case "SELECT": return { left: ["G", "IN0", "IN1"], right: ["OUT"] };
    case "MATH":
    case "WORD_LOGIC": return { left: ["IN1", "IN2"], right: ["OUT"] };
    case "LIMIT": return { left: ["MIN", "IN", "MAX"], right: ["OUT"] };
    case "CONVERT": return { left: cell.op === "NORM_X" || cell.op === "SCALE_X" ? ["MIN", "IN", "MAX"] : ["IN"], right: ["OUT"] };
    case "PROGRAM": return { left: ["EN"], right: ["ENO"] };
    default: return { left: ["IN"], right: ["Q"] };
  }
}

function FbdBlock({ cell, active, selected, onClick }: { cell: Cell; active: boolean; selected: boolean; onClick: () => void }) {
  const empty = cell.type === "EMPTY";
  if (empty) {
    return (
      <button
        onClick={onClick}
        className={`group relative flex h-8 w-12 shrink-0 items-center justify-center bg-transparent ${selected ? "z-10" : ""}`}
        title="إضافة Function Block"
      >
        <span className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-[#666]" />
        <span className={`relative grid h-4 w-4 place-items-center rounded-full border bg-white text-[11px] leading-none transition ${selected ? "border-[#008bd2] text-[#008bd2] ring-2 ring-[#008bd2]/20" : "border-[#8b8b8b] text-[#666] group-hover:border-[#008bd2] group-hover:text-[#008bd2]"}`}>+</span>
      </button>
    );
  }

  const ports = fbdPorts(cell);
  const rows = Math.max(2, ports.left.length, ports.right.length);
  const minHeight = Math.max(58, 30 + rows * 15);
  return (
    <button
      onClick={onClick}
      className={`relative shrink-0 bg-white text-left font-mono transition ${selected ? "z-10 ring-2 ring-[#008bd2] ring-offset-2" : ""}`}
      style={{ width: BLOCK_W, minHeight, border: `1px solid ${active ? "#00a651" : "#6e6e6e"}` }}
      title={`${cell.type} ${cell.operand}`}
    >
      <div className="border-b border-[#9a9a9a] bg-[#fafafa] px-1.5 py-1 text-center text-[10px] font-semibold text-[#222]">
        {blockTitle(cell)}
      </div>
      <div className="relative grid px-1 py-1.5" style={{ gridTemplateRows: `repeat(${rows}, 15px)` }}>
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="relative flex items-center justify-between text-[8px] leading-none text-[#333]">
            <span className="min-w-8 pr-1 text-left">{ports.left[i] ?? ""}</span>
            <span className="min-w-8 pl-1 text-right">{ports.right[i] ?? ""}</span>
            {ports.left[i] && <span className="absolute -left-[8px] top-1/2 h-px w-[7px] -translate-y-1/2 bg-[#555]" />}
            {ports.right[i] && <span className="absolute -right-[8px] top-1/2 h-px w-[7px] -translate-y-1/2 bg-[#555]" />}
          </div>
        ))}
      </div>
      {cell.operand && <div className="absolute -bottom-4 left-1/2 max-w-[130px] -translate-x-1/2 truncate bg-white px-1 text-[8px] text-[#333]">{cell.operand}</div>}
    </button>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="text-[10px]">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input className="sim-input w-full font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

export function FbdView() {
  useTick();
  const project = store.project;
  const monitoring = store.status === "RUN";
  const [edit, setEdit] = useState<{ net: string; r: number; c: number } | null>(null);
  const [search, setSearch] = useState("");

  const editing = edit ? project.networks.find((n) => n.id === edit.net)?.rows[edit.r]?.[edit.c] : null;

  const fbdSpecs = useMemo(() => TOOL_SPECS.filter((s) => s.type || s.action === "CLEAR"), []);
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const specs = q ? fbdSpecs.filter((s) => `${s.label} ${s.hint} ${s.group}`.toLowerCase().includes(q)) : fbdSpecs;
    return GROUP_ORDER.map((group) => ({ group, items: specs.filter((s) => s.group === group) })).filter((g) => g.items.length);
  }, [fbdSpecs, search]);

  const applySpec = (netId: string, r: number, c: number, spec: ToolSpec) => {
    store.update((p) => {
      const net = p.networks.find((n) => n.id === netId);
      if (!net) return;
      ensureColumn(net, c);
      if (spec.action === "CLEAR") {
        net.rows[r]![c] = emptyCell();
        return;
      }
      if (!spec.type) return;
      const current = net.rows[r]![c] ?? emptyCell();
      net.rows[r]![c] = makeCell(spec, current);
      keepSpareColumn(net, c);
    });
    setEdit({ net: netId, r, c });
  };

  const patch = (data: Partial<Cell>) => {
    if (!edit) return;
    store.update((p) => {
      const cell = p.networks.find((n) => n.id === edit.net)?.rows[edit.r]?.[edit.c];
      if (cell) Object.assign(cell, data);
    }, false);
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col" dir="ltr">
      <div className="flex-1 overflow-auto bg-[#e9e9e9] p-1.5 sm:p-2">
        <div className="space-y-1.5">
          {project.networks.map((net, ni) => {
            const cols = visibleCols(net);
            const pw = store.power[net.id];
            return (
              <section key={net.id} className="overflow-hidden border border-[#b7b7b7] bg-white">
                <div className="flex items-center border-b border-[#c7c7c7] bg-[#efefef] text-[10px] text-[#333]">
                  <div className="flex w-8 shrink-0 items-center justify-center border-r border-[#c7c7c7] py-1 font-mono text-[9px] text-[#666]">{ni + 1}</div>
                  <div className="min-w-0 flex-1 px-2 py-1" dir="ltr">
                    <span className="font-semibold">Network {ni + 1}</span>
                  </div>
                  {monitoring && <span className="mr-1 border border-[#00a651] bg-white px-1.5 py-0.5 text-[8px] font-semibold text-[#00843d]">MONITOR</span>}
                </div>

                <div className="flex items-center border-b border-[#e0e0e0] bg-[#fafafa] text-[9px]">
                  <div className="w-8 shrink-0 border-r border-[#dedede] py-1 text-center text-[#888]">▾</div>
                  <span className="w-11 shrink-0 px-2 text-[#666]">Title</span>
                  <input
                    className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1 text-[10px] font-medium text-[#222] outline-none"
                    value={net.title}
                    onChange={(e) => store.update((p) => { const n = p.networks.find((x) => x.id === net.id); if (n) n.title = e.target.value; }, false)}
                  />
                  <button className="mr-1 border border-[#bdbdbd] bg-[#f7f7f7] px-1.5 py-0.5 text-[8px] text-[#444] hover:bg-white" onClick={() => store.update((p) => {
                    const n = p.networks.find((x) => x.id === net.id);
                    if (!n) return;
                    const count = Math.max(2, ...n.rows.map((row) => row.length));
                    n.rows.push(emptyRow(count));
                    n.links.push(Array.from({ length: count + 1 }, () => false));
                  })}>+ مسار</button>
                  <button className="mr-1 border border-[#c8a2a2] bg-[#fffafa] px-1.5 py-0.5 text-[8px] text-[#a33] hover:bg-white" onClick={() => store.update((p) => { p.networks = p.networks.filter((x) => x.id !== net.id); })}>حذف</button>
                </div>

                <div className="relative min-h-[118px] overflow-x-auto bg-white py-5">
                  <div className="pointer-events-none absolute bottom-0 left-8 top-0 w-px bg-[#d0d0d0]" />
                  <div className="space-y-6 pl-10 pr-5">
                    {net.rows.map((row, r) => (
                      <div key={r} className="relative flex min-w-max items-center py-2">
                        <div className="h-px w-14 shrink-0" style={{ background: monitoring && Boolean(pw?.power[r]?.[0]) ? "#00a651" : "#5f5f5f" }} />
                        {Array.from({ length: cols }, (_, c) => {
                          const cell = row[c] ?? emptyCell();
                          const active = monitoring && Boolean(pw?.cellOn[r]?.[c]);
                          const lineHot = monitoring && Boolean(pw?.power[r]?.[c]);
                          const nextHot = monitoring && Boolean(pw?.power[r]?.[c + 1]);
                          return (
                            <div key={c} className="flex items-center">
                              {cell.type === "EMPTY" ? (
                                <FbdBlock cell={cell} active={active} selected={edit?.net === net.id && edit.r === r && edit.c === c} onClick={() => { setSearch(""); setEdit({ net: net.id, r, c }); }} />
                              ) : (
                                <>
                                  <div className="h-px w-7 shrink-0" style={{ background: lineHot ? "#00a651" : "#5f5f5f" }} />
                                  <FbdBlock cell={cell} active={active} selected={edit?.net === net.id && edit.r === r && edit.c === c} onClick={() => { setSearch(""); setEdit({ net: net.id, r, c }); }} />
                                  <div className="h-px w-7 shrink-0" style={{ background: nextHot ? "#00a651" : "#5f5f5f" }} />
                                </>
                              )}
                            </div>
                          );
                        })}
                        <div className="h-px w-16 shrink-0" style={{ background: monitoring && Boolean(pw?.power[r]?.[cols]) ? "#00a651" : "#5f5f5f" }} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex min-h-7 items-center border-t border-[#e2e2e2] bg-[#fafafa] px-10 py-1 text-[8px] text-[#777]" dir="rtl">
                  {net.comment || ""}
                </div>
              </section>
            );
          })}
          <button className="sim-btn" onClick={() => store.update((p) => { p.networks.push(newNetwork(p.networks.length + 1)); })}>+ Network جديدة</button>
        </div>
      </div>
      {edit && editing && (
        <div className="absolute right-2 top-2 z-50 flex max-h-[min(82vh,40rem)] w-[min(29rem,calc(100%-1rem))] flex-col overflow-hidden rounded-lg border border-border bg-card/95 shadow-2xl backdrop-blur" dir="rtl">
          <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold">{editing.type === "EMPTY" ? "إضافة Function Block" : "خصائص Function Block"}</div>
              <div className="font-mono text-[9px] text-muted-foreground">R{edit.r + 1} · C{edit.c + 1} · {blockTitle(editing)}</div>
            </div>
            <button className="sim-btn" onClick={() => setEdit(null)}>إغلاق</button>
          </div>

          {editing.type !== "EMPTY" && (
            <div className="space-y-2 border-b border-border p-2">
              <div className="grid grid-cols-2 gap-1.5">
                <Field label="OUT / Operand" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} />
                <Field label="Tag name" value={editing.operand ? store.tag(editing.operand) : ""} onChange={(v) => editing.operand && store.setTag(editing.operand, v)} />
              </div>

              {(["TON", "TOF", "TP", "TONR"].includes(editing.type)) && (
                <div className="grid grid-cols-2 gap-1.5"><Field label="PT" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v })} />{editing.type === "TONR" && <Field label="Reset" value={editing.reset ?? ""} onChange={(v) => patch({ reset: v.toUpperCase() })} />}</div>
              )}
              {(["CTU", "CTD", "CTUD"].includes(editing.type)) && (
                <div className="grid grid-cols-3 gap-1.5"><Field label="PV" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v })} /><Field label="Reset" value={editing.reset ?? ""} onChange={(v) => patch({ reset: v.toUpperCase() })} />{editing.type === "CTUD" && <Field label="CD" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} />}</div>
              )}
              {(editing.type === "SR" || editing.type === "RS") && (
                <div className="grid grid-cols-3 gap-1.5"><Field label="S" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} /><Field label="R" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} /><Field label="Q" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
              {editing.type === "BOOL" && (
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"><Field label="IN1" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} />{editing.op !== "NOT" && <Field label="IN2" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} />}{editing.op !== "NOT" && <Field label="IN3" value={editing.c ?? ""} onChange={(v) => patch({ c: v.toUpperCase() })} />}<Field label="OUT" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
              {editing.type === "CMP" && (
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"><Field label="VALUE / IN1" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /><Field label={editing.op === "IN_RANGE" || editing.op === "OUT_RANGE" ? "MIN" : "IN2"} value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} />{(editing.op === "IN_RANGE" || editing.op === "OUT_RANGE") && <Field label="MAX" value={editing.c ?? ""} onChange={(v) => patch({ c: v.toUpperCase() })} />}</div>
              )}
              {editing.type === "SELECT" && (
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"><Field label="G" value={editing.reset ?? ""} onChange={(v) => patch({ reset: v.toUpperCase() })} /><Field label="IN0" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} /><Field label="IN1" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} /><Field label="OUT" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
              {(editing.type === "MOVE" || editing.type === "MATH" || editing.type === "WORD_LOGIC") && (
                <div className="grid grid-cols-3 gap-1.5"><Field label="IN1" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} />{editing.type !== "MOVE" && editing.b !== undefined && <Field label="IN2" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} />}<Field label="OUT" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
              {editing.type === "LIMIT" && (
                <div className="grid grid-cols-4 gap-1.5"><Field label="MIN" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} /><Field label="IN" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} /><Field label="MAX" value={editing.c ?? ""} onChange={(v) => patch({ c: v.toUpperCase() })} /><Field label="OUT" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
              {editing.type === "CONVERT" && (
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4"><Field label="IN" value={editing.preset ?? ""} onChange={(v) => patch({ preset: v.toUpperCase() })} />{(editing.op === "NORM_X" || editing.op === "SCALE_X") && <Field label="MIN" value={editing.b ?? ""} onChange={(v) => patch({ b: v.toUpperCase() })} />}{(editing.op === "NORM_X" || editing.op === "SCALE_X") && <Field label="MAX" value={editing.c ?? ""} onChange={(v) => patch({ c: v.toUpperCase() })} />}<Field label="OUT" value={editing.operand} onChange={(v) => patch({ operand: v.toUpperCase() })} /></div>
              )}
            </div>
          )}

          <div className="border-b border-border p-2">
            <input className="sim-input w-full" placeholder="بحث في جميع بلوكات LAD / FBD..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            <div className="space-y-2">
              {filteredGroups.map(({ group, items }) => (
                <section key={group}>
                  <div className="mb-1 text-[9px] font-semibold text-muted-foreground">{group}</div>
                  <div className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                    {items.map((spec) => (
                      <button key={spec.id} className={`sim-btn min-h-11 justify-center px-1 text-[10px] font-mono ${spec.type === editing.type && (!spec.op || spec.op === editing.op) ? "border-primary bg-primary/10 text-primary" : ""}`} title={spec.hint} onClick={() => applySpec(edit.net, edit.r, edit.c, spec)}>
                        {spec.action === "CLEAR" ? "✕" : spec.label}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
          <div className="border-t border-border px-2 py-1.5 text-[9px] text-muted-foreground">اضغط على أي بلوك لتبديله أو تعديل خصائصه. التغييرات تظهر أيضًا في LAD لأن اللغتين تستخدمان نفس البرنامج.</div>
        </div>
      )}
    </div>
  );
}
